import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { AccountDTO } from '@invenco/accounts-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import type { Result } from '@/shared/helpers/Result';

const formSchema = z.object({
  warehouseId: z.string().min(1, 'Select a warehouse'),
  tenantName: z.string().min(1, 'Enter a tenant name'),
  tenantCode: z.string().min(1, 'Enter a tenant code'),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  account: Partial<AccountDTO>;
  onSubmit: (data: FormValues) => Promise<Result>;
};

export function InviteToWarehouseDialogContent({ account, onSubmit }: Props) {
  const { warehousingGateway } = useGateways();
  const defaultValues = useMemo(
    () => ({
      warehouseId: '',
      tenantName: account.name ?? '',
      tenantCode: '',
    }),
    [account.name],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { data: warehouses, isLoading: isLoadingWarehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: ({ signal }) => warehousingGateway.getWarehouses(undefined, { signal }),
  });

  return (
    <FormDialogContent form={form} onSubmit={async (data) => onSubmit(data)}>
      <DialogHeader>
        <DialogTitle>Invite to warehouse</DialogTitle>
        <DialogDescription>Create a warehouse tenant for this account.</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <SelectFormField
          control={form.control}
          name="warehouseId"
          label="Warehouse"
          placeholder={isLoadingWarehouses ? 'Loading warehouses...' : 'Select a warehouse'}
          disabled={isLoadingWarehouses}
          className="w-full"
        >
          {warehouses?.items?.map((warehouse) => (
            <SelectItem key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
            </SelectItem>
          ))}
        </SelectFormField>
        <TextFormField control={form.control} name="tenantName" label="Tenant name" />
        <TextFormField control={form.control} name="tenantCode" label="Tenant code" />
      </DialogBody>
    </FormDialogContent>
  );
}
